import { z } from "zod";
import { backdropSchema, type Backdrop } from "./backdrop";
import { ANSWERABLE_TYPES, type AnswerType } from "./answers";

/**
 * The frozen deck — Rule 6.
 *
 * A session captures its deck at creation and never reads `decks`, `slides` or
 * `slide_options` again. Everything the live path needs is in `sessions.deck_snapshot`.
 *
 * The rule exists because the alternative is genuinely dangerous, and quietly so. A
 * presenter editing next week's copy of a deck while today's session is running would
 * otherwise change the prompt on screen mid-round; worse, editing `is_correct` after
 * people have answered would silently rewrite what counted as right, retroactively, for
 * answers already scored. There is no error and no log line — just a leaderboard that
 * no longer matches what anyone saw.
 *
 * Note what the freeze also does: it NORMALISES. `slides.config` is an open jsonb blob,
 * and interpreting it (which key holds the time limit? what if it is a string?) is
 * exactly the kind of decision that must not happen on the hot path or, worse, happen
 * differently in two places. It is resolved once, here, into explicit typed fields.
 */

export const SLIDE_TYPES = [
  "mcq",
  "true_false",
  "type_answer",
  "poll",
  "word_cloud",
  "scale",
  "open_text",
  "qna",
  "content",
] as const;

export type SlideType = (typeof SLIDE_TYPES)[number];

export const slideTypeSchema = z.enum(SLIDE_TYPES);

/** Is this slide type one a participant can answer? Derived from the answer union. */
export function isAnswerable(type: SlideType): type is AnswerType {
  return (ANSWERABLE_TYPES as readonly string[]).includes(type);
}

const uuid = z.string().uuid();

export const frozenOptionSchema = z
  .object({
    id: uuid,
    position: z.number().int().nonnegative(),
    label: z.string(),
    /**
     * Frozen deliberately. This is the field whose later edit would rewrite history.
     */
    isCorrect: z.boolean(),
    mediaUrl: z.string().nullable(),
  })
  .strict();

export type FrozenOption = z.infer<typeof frozenOptionSchema>;

/**
 * How long a quiz question is shown on its own before its answers open: `auto` for a
 * wait that follows the question's length, or a fixed number of seconds (0, none).
 */
export const READING_TIMES = ["auto", 0, 3, 5] as const;
export const readingTimeSchema = z.union([z.literal("auto"), z.literal(0), z.literal(3), z.literal(5)]);
export type ReadingTime = z.infer<typeof readingTimeSchema>;

/**
 * The wait, in milliseconds, between a question appearing and its answers opening.
 *
 * Only on scored questions: they race the clock, and the clock should start once the
 * room has read what is asked, not while it is still reading. A poll or word cloud
 * opens at once. `auto` gives 3 s to a short question (up to 60 characters), 4 s up to
 * 120, and 5 s to anything longer, up to the longest a question may be (TEXT_LIMITS).
 */
export function readingDelayMs(slide: { type: SlideType; isScored: boolean; prompt: string; readingTime: ReadingTime }): number {
  if (!slide.isScored || !isAnswerable(slide.type)) return 0;
  if (slide.readingTime !== "auto") return slide.readingTime * 1000;
  const length = slide.prompt.trim().length;
  return (length <= 60 ? 3 : length <= 120 ? 4 : 5) * 1000;
}

export const frozenSlideSchema = z
  .object({
    id: uuid,
    position: z.number().int().nonnegative(),
    type: slideTypeSchema,
    prompt: z.string(),
    mediaUrl: z.string().nullable(),

    isScored: z.boolean(),
    /**
     * 0 means untimed — the round stays open until the presenter closes it, and
     * scoring awards full points regardless of when the answer arrived. Both
     * behaviours already follow from 0 in the scoring package and the round store, so
     * "untimed" needs no separate flag to fall out of sync with.
     */
    timeLimitMs: z.number().int().nonnegative().max(3_600_000),
    basePoints: z.number().int().nonnegative().max(100_000),
    /** Double-points rounds and the like. 1 unless the deck says otherwise. */
    multiplier: z.number().positive().max(10),

    /**
     * The bounds a scale answer must fall within, and how many words a word cloud
     * accepts.
     *
     * Frozen with the slide because they are VALIDATION inputs, not presentation: the
     * server checks every answer against them, and a check that reads from the live
     * deck would be a check that changes mid-round.
     */
    scaleMin: z.number().int(),
    scaleMax: z.number().int(),
    maxWords: z.number().int().min(1).max(10),

    /**
     * mcq and poll: may a participant pick more than one option? Off, the server takes
     * exactly one. Defaulted: snapshots frozen before the setting existed took one, as
     * their phones only ever sent one.
     */
    multiSelect: z.boolean().default(false),
    /**
     * type_answer: the answers that count as right (scoring's matchesAcceptedAnswer).
     * Frozen for the same reason as `isCorrect`, and like it never sent to a phone
     * before the results are revealed.
     */
    acceptedAnswers: z.array(z.string()).default([]),
    /** A picture behind this slide alone, in place of the theme's. Defaulted: snapshots from before slides had one. */
    background: backdropSchema.nullable().default(null),
    /**
     * A quiz question's moment to read before its answers open (readingDelayMs).
     * Defaulted to none: a snapshot frozen before the setting existed runs as it did.
     */
    readingTime: readingTimeSchema.default(0),

    options: z.array(frozenOptionSchema),
  })
  .strict();

export type FrozenSlide = z.infer<typeof frozenSlideSchema>;

export const deckSnapshotSchema = z
  .object({
    /**
     * Bumped when the shape changes. Sessions outlive deployments — a session started
     * before a rolling upgrade is still live after it — so the reader has to be able
     * to tell which shape it is holding rather than guess from which keys are present.
     */
    version: z.literal(1),
    frozenAt: z.string().datetime(),
    /** Null once the source deck is deleted. The snapshot survives it; that is the point. */
    deckId: uuid.nullable(),
    title: z.string(),
    theme: z.record(z.string(), z.unknown()),
    /** The lobby's settings (lobby.ts). Defaulted: snapshots frozen before lobbies had any. */
    lobby: z.record(z.string(), z.unknown()).default({}),
    slides: z.array(frozenSlideSchema),
  })
  .strict();

export type DeckSnapshot = z.infer<typeof deckSnapshotSchema>;

/** A slide's time limit when its stored config has none: what decks saved that way have always run on. */
export const DEFAULT_TIME_LIMIT_MS = 30_000;
/** The time a new quiz question gets — in the editor, a template, an import or the starter deck — until someone changes it. */
export const DEFAULT_QUIZ_TIME_LIMIT_MS = 15_000;
export const DEFAULT_BASE_POINTS = 1000;
export const DEFAULT_SCALE_MIN = 1;
export const DEFAULT_SCALE_MAX = 10;
export const DEFAULT_MAX_WORDS = 3;

/** Coerce one config value, falling back on anything unusable. */
function finite(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === "string" ? Number(value) : value;
  if (typeof n !== "number" || !Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, min), max);
}

/**
 * Pull the live-round settings out of a slide's `config` blob.
 *
 * Tolerant on the way in, strict on the way out: config is authored by the deck editor
 * and by the public API, so a missing key or a numeric string is expected, while
 * everything downstream gets a plain number it can rely on.
 *
 * Anything unusable becomes the default rather than NaN, which matters more than it
 * looks: the round store's expiry check is `responseMs > timeLimitMs`, and that
 * comparison is FALSE for NaN — a malformed config would leave the round accepting
 * answers forever, with nothing in the logs to say why.
 */
export function readSlideConfig(config: unknown): {
  timeLimitMs: number;
  basePoints: number;
  multiplier: number;
  scaleMin: number;
  scaleMax: number;
  maxWords: number;
  multiSelect: boolean;
  acceptedAnswers: string[];
  background: Backdrop | null;
  readingTime: ReadingTime;
} {
  const c = (config ?? {}) as Record<string, unknown>;

  // Read before the return so the two can be reconciled: a deck with min above max
  // would otherwise reject every possible answer, and the participant would be told
  // only that their answer was invalid.
  const scaleMin = Math.round(finite(c["scaleMin"], DEFAULT_SCALE_MIN, -1000, 1000));
  const scaleMax = Math.round(finite(c["scaleMax"], DEFAULT_SCALE_MAX, -1000, 1000));

  return {
    scaleMin: Math.min(scaleMin, scaleMax),
    scaleMax: Math.max(scaleMin, scaleMax),
    // Bounded by the answer schema's own limit of 10; a deck asking for more would
    // describe a slide whose answers the contract rejects.
    maxWords: Math.round(finite(c["maxWords"], DEFAULT_MAX_WORDS, 1, 10)),
    // Rounded: both are integers everywhere downstream, including in Valkey where they
    // are stored as strings and parsed back.
    timeLimitMs: Math.round(finite(c["timeLimitMs"], DEFAULT_TIME_LIMIT_MS, 0, 3_600_000)),
    basePoints: Math.round(finite(c["basePoints"], DEFAULT_BASE_POINTS, 0, 100_000)),
    // Not rounded — 1.5× is a real thing a deck asks for. Zero (and anything negative,
    // which clamps to zero) falls back to 1: no deck expresses "this round is worth
    // nothing" by setting a multiplier, and silently zeroing a whole round's scores is
    // the kind of bug nobody reports until the leaderboard is already on screen.
    multiplier: finite(c["multiplier"], 1, 0, 10) || 1,
    multiSelect: c["multiSelect"] === true,
    // Bounded like the deck contract's input: ten answers of up to 200 characters, the
    // longest a participant can type. Anything else in the array is dropped.
    acceptedAnswers: Array.isArray(c["acceptedAnswers"])
      ? c["acceptedAnswers"]
          .filter((a): a is string => typeof a === "string" && a.trim() !== "")
          .map((a) => a.trim().slice(0, 200))
          .slice(0, 10)
      : [],
    background: backdropSchema.nullable().catch(null).parse(c["background"] ?? null),
    // Absent is the default, auto: a question written before the setting gets the wait too.
    readingTime: readingTimeSchema.catch("auto").parse(c["readingTime"] ?? "auto"),
  };
}

/**
 * A slide as a PARTICIPANT sees it — everything needed to show and answer it, and
 * nothing that would give the answer away.
 *
 * Built by `toPublicSlide`, never by spreading a frozen slide: `isCorrect` is on every
 * option of a scored question, and `acceptedAnswers` on a typed one, and a spread would
 * publish them to the whole room.
 */
export const publicSlideSchema = z
  .object({
    id: uuid,
    position: z.number().int().nonnegative(),
    type: slideTypeSchema,
    prompt: z.string(),
    mediaUrl: z.string().nullable(),
    isScored: z.boolean(),
    timeLimitMs: z.number().int().nonnegative(),
    scaleMin: z.number().int(),
    scaleMax: z.number().int(),
    maxWords: z.number().int().min(1).max(10),
    /** Defaulted: a current slide recorded in Valkey before the setting existed. */
    multiSelect: z.boolean().default(false),
    options: z.array(
      z.object({ id: uuid, label: z.string(), mediaUrl: z.string().nullable() }).strict(),
    ),
  })
  .strict();

export type PublicSlide = z.infer<typeof publicSlideSchema>;

export function toPublicSlide(slide: FrozenSlide): PublicSlide {
  return {
    id: slide.id,
    position: slide.position,
    type: slide.type,
    prompt: slide.prompt,
    mediaUrl: slide.mediaUrl,
    isScored: slide.isScored,
    timeLimitMs: slide.timeLimitMs,
    scaleMin: slide.scaleMin,
    scaleMax: slide.scaleMax,
    maxWords: slide.maxWords,
    multiSelect: slide.multiSelect,
    // Never acceptedAnswers: on a type_answer slide they are the answer.
    options: slide.options.map((o) => ({ id: o.id, label: o.label, mediaUrl: o.mediaUrl })),
  };
}
